import type { LayerStatus } from "./layerStatus";
import { computeLayerStatus, computeLeafStatus } from "./layerStatus";
import type { DragonTile, InnerTile, TileStatus } from "@/types/dragon-tiles";

import {
  displayInnerTileName,
  hasAudienceId,
} from "@/features/DragonTiles/audienceInnerTiles";

export interface FrameRollupInput {
  id: string;
  isActive: boolean | null | undefined;
  eventIds: string[];
}

export interface InnerTileRollupInput {
  id: string;
  name: string;
  isActive: boolean;
  isPassThrough: boolean;
  frames: FrameRollupInput[];
}

export interface EventRollupInput {
  id: string;
  name: string;
  enabled: boolean;
}

export interface DragonTileRollupInput {
  slug: string;
  isActive: boolean;
  innerTiles: InnerTileRollupInput[];
  events?: EventRollupInput[];
  framesLoaded: boolean;
}

export type LayerKind = "dragonTile" | "event" | "audience";

export type ChildKind = "event" | "audience" | "tile" | "orphan";

export interface ChildReport {
  kind: ChildKind;
  id: string;
  name: string;
  status: LayerStatus;
  report?: LayerReport;
}

export interface LayerReport {
  kind: LayerKind;
  status: LayerStatus;
  ownToggle: boolean;
  children: ChildReport[];
}

export function isBroadcastDragonTile(tile: Pick<DragonTile, "slug">): boolean {
  return tile.slug === "broadcast";
}

export function computeFrameStatus(frame: FrameRollupInput): LayerStatus {
  return computeLeafStatus(frame.isActive ?? true);
}

type FrameScope =
  | { kind: "all" }
  | { kind: "orphan"; layerEventIds: Set<string> }
  | { kind: "event"; eventId: string };

function isOrphanFrame(
  frame: FrameRollupInput,
  layerEventIds: Set<string>,
): boolean {
  if (frame.eventIds.length === 0) return true;
  return !frame.eventIds.some((eventId) => layerEventIds.has(eventId));
}

function scopeSelectsFrame(
  scope: FrameScope,
  frame: FrameRollupInput,
): boolean {
  switch (scope.kind) {
    case "all":
      return true;
    case "orphan":
      return isOrphanFrame(frame, scope.layerEventIds);
    case "event":
      return frame.eventIds.includes(scope.eventId);
  }
}

function frameChild(
  frame: FrameRollupInput,
  kind: "tile" | "orphan",
): ChildReport {
  return {
    kind,
    id: frame.id,
    name: frame.id,
    status: computeFrameStatus(frame),
  };
}

function audienceReport(
  innerTile: InnerTileRollupInput,
  frames: FrameRollupInput[],
): LayerReport {
  const children = frames.map((frame) => frameChild(frame, "tile"));
  return {
    kind: "audience",
    ownToggle: innerTile.isActive,
    children,
    status: computeLayerStatus(
      innerTile.isActive,
      children.map((child) => child.status),
    ),
  };
}

function audienceChild(
  innerTile: InnerTileRollupInput,
  report: LayerReport,
): ChildReport {
  return {
    kind: "audience",
    id: innerTile.id,
    name: innerTile.name,
    status: report.status,
    report,
  };
}

function innerTileChildren(
  innerTiles: InnerTileRollupInput[],
  scope: FrameScope,
): ChildReport[] {
  const children: ChildReport[] = [];
  const passThroughKind = scope.kind === "orphan" ? "orphan" : "tile";
  for (const innerTile of innerTiles) {
    const frames = innerTile.frames.filter((frame) =>
      scopeSelectsFrame(scope, frame),
    );
    if (innerTile.isPassThrough) {
      children.push(
        ...frames.map((frame) => frameChild(frame, passThroughKind)),
      );
      continue;
    }
    if (frames.length === 0) {
      if (scope.kind !== "event" && innerTile.frames.length === 0) {
        children.push(audienceChild(innerTile, audienceReport(innerTile, [])));
      }
      continue;
    }
    children.push(audienceChild(innerTile, audienceReport(innerTile, frames)));
  }
  return children;
}

export function buildInnerTileReport(
  innerTile: InnerTileRollupInput,
): LayerReport {
  return audienceReport(innerTile, innerTile.frames);
}

export function buildEventReport(
  event: EventRollupInput,
  innerTiles: InnerTileRollupInput[],
): LayerReport {
  const children = innerTileChildren(innerTiles, {
    kind: "event",
    eventId: event.id,
  });
  return {
    kind: "event",
    ownToggle: event.enabled,
    children,
    status: computeLayerStatus(
      event.enabled,
      children.map((child) => child.status),
    ),
  };
}

export function buildDragonTileReport(
  input: DragonTileRollupInput,
): LayerReport | null {
  if (isBroadcastDragonTile(input)) return null;
  if (!input.framesLoaded) return null;

  const children: ChildReport[] =
    input.events === undefined
      ? innerTileChildren(input.innerTiles, { kind: "all" })
      : [
          ...input.events.map((event): ChildReport => {
            const report = buildEventReport(event, input.innerTiles);
            return {
              kind: "event",
              id: event.id,
              name: event.name,
              status: report.status,
              report,
            };
          }),
          ...innerTileChildren(input.innerTiles, {
            kind: "orphan",
            layerEventIds: new Set(input.events.map((event) => event.id)),
          }),
        ];

  return {
    kind: "dragonTile",
    ownToggle: input.isActive,
    children,
    status: computeLayerStatus(
      input.isActive,
      children.map((child) => child.status),
    ),
  };
}

export function computeInnerTileStatus(
  innerTile: InnerTileRollupInput,
): LayerStatus {
  return buildInnerTileReport(innerTile).status;
}

export function computeEventStatus(
  event: EventRollupInput,
  innerTiles: InnerTileRollupInput[],
): LayerStatus {
  return buildEventReport(event, innerTiles).status;
}

export function computeDragonTileStatus(
  input: DragonTileRollupInput,
): LayerStatus | null {
  return buildDragonTileReport(input)?.status ?? null;
}

export function toTileStatus(status: LayerStatus): TileStatus {
  return status === "NeedsAttention" ? "Needs Attention" : status;
}

export function toInnerTileRollupInput(
  innerTile: InnerTile,
): InnerTileRollupInput {
  return {
    id: innerTile.id,
    name: displayInnerTileName(innerTile),
    isActive: innerTile.is_active,
    isPassThrough: !hasAudienceId(innerTile),
    frames: innerTile.frames.map((frame) => ({
      id: frame.id,
      isActive: frame.is_active,
      eventIds: (frame.events ?? [])
        .filter((event) => event.active === 1)
        .map((event) => event.name),
    })),
  };
}

export function toDragonTileRollupInput(
  tile: DragonTile,
  options: {
    framesLoaded: boolean;
    events?: EventRollupInput[];
  },
): DragonTileRollupInput {
  return {
    slug: tile.slug,
    isActive: tile.is_active,
    innerTiles: tile.inner_tiles.map(toInnerTileRollupInput),
    events: options.events,
    framesLoaded: options.framesLoaded,
  };
}
